import { Sound, SoundManager } from '@1x2network/howler_sound'
import { CreateSoundOptions } from '@1x2network/howler_sound/lib/sound/createSound'
import { Specification } from '@1x2network/pixi_assets'
import gsap from 'gsap'
import { BackgroundMusic, BackgroundMusicSpecification } from '../utils/BackgroundMusic'

/**
 * Class for playing sounds
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class SoundPlayer extends SoundManager {
    private specification: SoundSpecification
    private background: BackgroundMusic
    private currentTier = -1
    private transition: Sound
    private hawStart: Sound
    private hawEnd: Sound
    private winTiers: Sound[]
    private symbolWin: Sound[]
    private reelLand: Sound
    private spinStart: Sound
    private potUpgrade: Sound
    private potAnimate: Sound
    private scatterLand: Sound
    private scatterTrigger: Sound
    private collectorTrigger: Sound
    private collectorLand: Sound
    private coinShowerEmit: Sound
    private coinShowerLand: Sound
    private hawSummaryIncrease: Sound
    private collectorIncrease: Sound
    private countUpLoop: Sound
    private countUpEnd: Sound
    private anticipation: Sound
    private anticipationMiss: Sound
    private falsePersistenceLoops: Sound[]
    private trainTrackBuilding: Sound
    private trainPassing: Sound

    private _debounce = new Map<string, number>()

    constructor() {
        super()
        this.specification = Specification.get('sound') as SoundSpecification
        this.background = new BackgroundMusic(this.specification.background)
        this.transition = new Sound(this.specification.transition)
        this.hawStart = new Sound(this.specification.hawStart)
        this.hawEnd = new Sound(this.specification.hawEnd)
        this.winTiers = this.specification.winTiers.map((spec) => new Sound(spec))
        this.symbolWin = this.specification.symbolWin.map((spec) => new Sound(spec))
        this.reelLand = new Sound(this.specification.reelLand)
        this.spinStart = new Sound(this.specification.spinStart)
        this.potUpgrade = new Sound(this.specification.potUpgrade)
        this.potAnimate = new Sound(this.specification.potAnimate)
        this.scatterLand = new Sound(this.specification.scatterLand)
        this.scatterTrigger = new Sound(this.specification.scatterTrigger)
        this.collectorTrigger = new Sound(this.specification.collectorTrigger)
        this.coinShowerEmit = new Sound(this.specification.coinShowerEmit)
        this.coinShowerLand = new Sound(this.specification.coinShowerLand)
        this.hawSummaryIncrease = new Sound(this.specification.hawSummaryIncrease)
        this.collectorIncrease = new Sound(this.specification.collectorIncrease)
        this.countUpLoop = new Sound(this.specification.countUpLoop)
        this.countUpEnd = new Sound(this.specification.countUpEnd)
        this.anticipation = new Sound(this.specification.anticipation)
        this.anticipationMiss = new Sound(this.specification.anticipationMiss)
        this.collectorLand = new Sound(this.specification.collectorLand)
        this.falsePersistenceLoops = this.specification.falsePersistenceLoops.map((spec) => new Sound(spec))
        this.trainTrackBuilding = new Sound(this.specification.trainTrackBuilding)
        this.trainPassing = new Sound(this.specification.trainPassing)
    }

    private debounce(sound: Sound): boolean {
        const { key, debounce = 0 } = (sound as unknown as { _options: CreateSoundOptions })._options
        const lastPlayTime = this._debounce.get(key) ?? -Infinity

        const now = performance.now()
        const timeSinceLastPlay = now - lastPlayTime
        const hasEnoughTimePassed = timeSinceLastPlay >= debounce

        if (hasEnoughTimePassed) {
            this._debounce.set(key, now)
        }

        return !hasEnoughTimePassed
    }

    public toggleSounds(enabled: boolean): void {
        this.enabled = enabled
    }

    public setTier(tier: number): void {
        if (this.currentTier === tier) return
        this.background.setTier(tier)
    }

    public playWinTier(tier: number) {
        const sound = this.winTiers[tier]
        sound.play()
    }

    public playWin(ids: number[]): void {
        const wildID = 8
        const includesWild = ids.includes(wildID)
        const excludesWild = ids.filter((id) => id !== wildID)

        if (includesWild) {
            this.symbolWin[wildID].play()
        }

        if (excludesWild.length) {
            this.symbolWin[excludesWild[0]].play()
        }
    }

    public playReelLand(): void {
        if (this.debounce(this.reelLand)) {
            return
        }
        this.reelLand = new Sound(this.specification.reelLand)
        this.reelLand.play()
    }

    public playSpinStart(): void {
        this.spinStart.play()
    }

    public fadeBackground(volume: number): void {
        this.background.fade(volume, 0.5)
    }

    public stopWinTier(tier: number) {
        const sound = this.winTiers[tier]
        if (!sound) return

        gsap.to(sound, {
            volume: 0,
            duration: 0.3,
            onComplete: () => {
                sound.stop()
                sound.volume = 1
            },
        })
    }

    public playPotUpgrade(): void {
        this.potUpgrade.play()
    }

    public playTransitionIn(): void {
        this.transition.play()
    }

    public playTransitionOut(): void {
        this.transition.play()
    }

    public playPotAnimate(i: number): void {
        this.potAnimate.play()
        this.potAnimate.rate = 1 + i * 0.015
    }

    public playScatterLand(i: number): void {
        if (this.debounce(this.scatterLand)) {
            return
        }

        this.scatterLand = new Sound(this.specification.scatterLand)
        this.scatterLand.play()
        this.scatterLand.rate = 1 + i * 0.03
    }

    public playScatterTrigger(): void {
        this.scatterTrigger.play()
    }

    public playCollectorTrigger(): void {
        this.collectorTrigger.play()
    }

    public playCoinShowerEmit(): void {
        this.coinShowerEmit.play()
    }

    public playCoinShowerLand(): void {
        this.coinShowerLand.play()
    }

    public playHawStart(): void {
        this.hawStart.play()
    }

    public playHawEnd(): void {
        this.hawEnd.play()
    }

    public playHawSummaryIncrease(): void {
        this.hawSummaryIncrease.play()
    }

    public playCollectorIncrease(i: number): void {
        if (this.debounce(this.collectorIncrease)) {
            return
        }

        this.collectorIncrease = new Sound(this.specification.collectorIncrease)
        this.collectorIncrease.play()
        this.collectorIncrease.rate = 1 + i * 0.02
    }

    public playCountUpLoop(): void {
        this.countUpLoop.play()
    }

    public stopCountUpLoop(): void {
        this.countUpLoop.stop()
    }

    public playCountUpEnd(): void {
        this.countUpEnd.play()
    }

    public playAnticipation(): void {
        if (!this.anticipation.playing) {
            this.anticipation.play()
        }
    }

    public stopAnticipation(): void {
        this.anticipation.stop()
    }

    public playAnticipationMiss(): void {
        this.anticipationMiss.play()
    }

    public playCollectorLand(i: number): void {
        if (this.debounce(this.collectorLand)) {
            return
        }

        this.collectorLand = new Sound(this.specification.collectorLand)
        this.collectorLand.play()
        this.collectorLand.rate = 1 + i * 0.03
    }

    public playFalsePersistenceLoop(tier: number): void {
        this.falsePersistenceLoops.forEach((sound) => sound.stop())
        const sound = this.falsePersistenceLoops[tier]
        if (sound) {
            sound.play()
        }
    }

    public playTrainTrackBuilding(): void {
        this.trainTrackBuilding.play()
    }

    public playTrainPassing(): void {
        const options = this.trainPassing.options as CreateSoundOptions & { maxVolume: number }
        this.trainPassing.volume = options.maxVolume
        this.trainPassing.play()
    }

    public stopTrainPassing(): void {
        const options = this.trainPassing.options as CreateSoundOptions & { maxVolume: number }
        this.trainPassing.fade(options.maxVolume, 0, 0.4)
    }
}

export interface SoundSpecification {
    background: BackgroundMusicSpecification
    transition: CreateSoundOptions
    hawStart: CreateSoundOptions
    hawEnd: CreateSoundOptions
    winTiers: CreateSoundOptions[]
    reelLand: CreateSoundOptions
    spinStart: CreateSoundOptions
    symbolWin: CreateSoundOptions[]
    potUpgrade: CreateSoundOptions
    potAnimate: CreateSoundOptions
    scatterLand: CreateSoundOptions
    scatterTrigger: CreateSoundOptions
    collectorTrigger: CreateSoundOptions
    collectorLand: CreateSoundOptions
    coinShowerEmit: CreateSoundOptions
    coinShowerLand: CreateSoundOptions
    hawSummaryIncrease: CreateSoundOptions
    collectorIncrease: CreateSoundOptions
    countUpLoop: CreateSoundOptions
    countUpEnd: CreateSoundOptions
    anticipation: CreateSoundOptions
    anticipationMiss: CreateSoundOptions
    falsePersistenceLoops: CreateSoundOptions[]
    trainTrackBuilding: CreateSoundOptions
    trainPassing: CreateSoundOptions & { maxVolume: number }
}
